/**
 * 74.7 (DECISIONS 348): the system's star field drawn in the sky — presentation only; reads a `StarField`
 * (core/starField.ts, pure) and draws it as one Points layer about the eye, each star's size and brightness by its
 * magnitude against the sky's limiting magnitude this frame (`limitingMagnitude(skyBrightness(daylight, air, floor))`):
 * the stars fade at dusk the faintest first, as 74.1's bodies fade by brightness, and an airless world shows them by day
 * clear of the sun's glare. The dozen brightest carry a halo (a second Points, additive); the companion sun, when there is
 * one, a bigger halo still. The faint end is floored in size and brightness (DECISIONS 352: the field reads full, not a
 * scatter of dim dots). Cost: two draws, ~6k points, no CPU work a frame beyond six uniforms.
 */
import * as THREE from 'three';
import { limitingMagnitude, skyBrightness, NIGHT_FLOOR, STAR_FIELD, type StarField, type StarClass } from '../core/starField';
import { SUN_TAU, SUN_ELEVATION_FLOOR } from '../core/air';

export const STAR_COLORS: Record<StarClass, string> = { blue: '#9bb4ff', white: '#f2f5ff', yellow: '#fff2d2', orange: '#ffd0a0', red: '#ffb48a' };
/** the point's size (px) at magnitude 0, and the faintest drawn size */
export const STAR_PX = { atZero: 7.5, min: 2.0, haloAtZero: 34 };
/** DECISIONS 352: the faint end floored — a star under the limit never draws fainter than this, so the field reads full */
export const STAR_ALPHA_FLOOR = 0.35;

export interface StarSkyFrame {
  camera: THREE.PerspectiveCamera;
  eye: THREE.Vector3;
  /** the system frame → world (as the system sky's) */
  frame: THREE.Quaternion;
  distance: number;
  daylight: number; air: number;
  /** the night floor (NIGHT_FLOOR.today or .proposed) */
  floor: number;
  /** the sun's direction (world) and its drawn radius (rad): the glare hides the stars about it by day */
  sunDir: THREE.Vector3; sunRadius: number;
  skyColor: THREE.Color;
  /** the renderer's pixel ratio (point sizes are in device pixels) */
  pixelRatio: number;
  /** 74.10 step 2b: the local up (world) and the air's density at the viewer — each star reddens and dims by its elevation as the sun does (core/air.ts sunExtinction); absent or 0: no air */
  up?: THREE.Vector3 | null; density?: number;
}

export class StarSkyLayer {
  readonly group = new THREE.Group();
  private readonly points: THREE.Points;
  private readonly halos: THREE.Points;
  private readonly mat: THREE.ShaderMaterial;
  private readonly haloMat: THREE.ShaderMaterial;
  /** this frame's limiting magnitude and how many stars are shown over a tenth */
  limit = 6.5;
  shown = 0;
  private readonly mags: Float32Array;

  constructor(readonly field: StarField) {
    const n = field.stars.length;
    const pos = new Float32Array(n * 3), col = new Float32Array(n * 3), mag = new Float32Array(n);
    const C = new THREE.Color();
    field.stars.forEach((s, i) => {
      pos[i * 3] = s.dir.x; pos[i * 3 + 1] = s.dir.y; pos[i * 3 + 2] = s.dir.z;
      C.set(STAR_COLORS[s.cls]); col[i * 3] = C.r; col[i * 3 + 1] = C.g; col[i * 3 + 2] = C.b;
      mag[i] = s.mag;
    });
    this.mags = mag;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3)); geo.setAttribute('color', new THREE.BufferAttribute(col, 3)); geo.setAttribute('mag', new THREE.BufferAttribute(mag, 1));
    const uniforms = { uLimit: { value: 6.5 }, uScale: { value: 1 }, uSun: { value: new THREE.Vector3(0, 1, 0) }, uGlare: { value: 0 }, uSky: { value: new THREE.Color() }, uHaze: { value: 0 }, uHalo: { value: 0 }, uUp: { value: new THREE.Vector3(0, 1, 0) }, uDensity: { value: 0 } };
    const vertex = /* glsl */`
      attribute float mag; varying vec3 vColor; varying float vA;
      uniform float uLimit, uScale, uGlare, uHalo, uDensity; uniform vec3 uSun, uUp;
      void main() {
        // 74.10 step 2b: through the air a low star comes red and dim, as the sun does (core/air.ts sunExtinction: the same τ, air mass 1 / max(floor, sin elevation), relative to overhead)
        float el = clamp(dot(normalize(position), uUp), 0.0, 1.0);
        vec3 ext = exp(-vec3(${SUN_TAU.r.toFixed(3)}, ${SUN_TAU.g.toFixed(3)}, ${SUN_TAU.b.toFixed(3)}) * uDensity * (1.0 / max(${SUN_ELEVATION_FLOOR.toFixed(3)}, el) - 1.0));
        vColor = color * ext;
        float t = clamp((uLimit + 0.5 - mag) / 1.5, 0.0, 1.0);
        float shown = t * t * (3.0 - 2.0 * t);
        shown = max(shown, ${STAR_ALPHA_FLOOR.toFixed(2)} * (1.0 - smoothstep(uLimit, uLimit + 0.5, mag)));   // the faint end floored (352)
        // the sun's glare: a star within the glare's angle of the sun is lost by day
        float toSun = acos(clamp(dot(normalize(position), uSun), -1.0, 1.0));
        shown *= smoothstep(uGlare * 0.5, uGlare, toSun);
        float px = ${STAR_PX.atZero.toFixed(1)} * pow(10.0, -0.2 * mag);
        if (uHalo > 0.5) { px = ${STAR_PX.haloAtZero.toFixed(1)} * pow(10.0, -0.12 * mag); shown *= 0.7; }
        vA = shown * dot(ext, vec3(0.2126, 0.7152, 0.0722));   // dimmer by the extinction's luminance
        gl_PointSize = max(${STAR_PX.min.toFixed(1)}, min(uHalo > 0.5 ? 64.0 : 9.0, px)) * uScale;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`;
    const fragment = /* glsl */`
      varying vec3 vColor; varying float vA; uniform vec3 uSky; uniform float uHaze, uHalo;
      void main() {
        vec2 d = gl_PointCoord - 0.5; float r = length(d) * 2.0;
        float a = uHalo > 0.5 ? pow(max(0.0, 1.0 - r), 2.2) : smoothstep(1.0, 0.3, r);
        vec3 c = mix(vColor, uSky, uHaze) * (uHalo > 0.5 ? 1.0 : 1.35);   // (a point under the tone map reads dim: lifted)
        gl_FragColor = vec4(c, a * vA);
        #include <colorspace_fragment>
      }`;
    this.mat = new THREE.ShaderMaterial({ uniforms, vertexShader: vertex, fragmentShader: fragment, vertexColors: true, transparent: true, depthWrite: false, depthTest: true });
    this.points = new THREE.Points(geo, this.mat);
    this.points.frustumCulled = false; this.points.renderOrder = 0; this.points.name = 'starSky.stars';
    this.group.add(this.points);
    // the halos: the named brightest, and the companion sun
    const bright = field.brightest.slice();
    const hn = bright.length + (field.companion ? 1 : 0);
    const hpos = new Float32Array(hn * 3), hcol = new Float32Array(hn * 3), hmag = new Float32Array(hn);
    bright.forEach((i, k) => { const s = field.stars[i]; hpos[k * 3] = s.dir.x; hpos[k * 3 + 1] = s.dir.y; hpos[k * 3 + 2] = s.dir.z; C.set(STAR_COLORS[s.cls]); hcol[k * 3] = C.r; hcol[k * 3 + 1] = C.g; hcol[k * 3 + 2] = C.b; hmag[k] = s.mag; });
    if (field.companion) { const k = bright.length, c = field.companion; hpos[k * 3] = c.dir.x; hpos[k * 3 + 1] = c.dir.y; hpos[k * 3 + 2] = c.dir.z; C.set(c.type === 'redDwarf' ? '#ff8866' : c.type === 'orangeDwarf' ? '#ffc080' : '#fff4e0'); hcol[k * 3] = C.r; hcol[k * 3 + 1] = C.g; hcol[k * 3 + 2] = C.b; hmag[k] = Math.max(-12, c.mag); }
    const hgeo = new THREE.BufferGeometry();
    hgeo.setAttribute('position', new THREE.BufferAttribute(hpos, 3)); hgeo.setAttribute('color', new THREE.BufferAttribute(hcol, 3)); hgeo.setAttribute('mag', new THREE.BufferAttribute(hmag, 1));
    this.haloMat = new THREE.ShaderMaterial({ uniforms: { ...uniforms, uHalo: { value: 1 } }, vertexShader: vertex, fragmentShader: fragment, vertexColors: true, transparent: true, depthWrite: false, depthTest: true, blending: THREE.AdditiveBlending });
    this.halos = new THREE.Points(hgeo, this.haloMat);
    this.halos.frustumCulled = false; this.halos.renderOrder = 0; this.halos.name = 'starSky.halos';
    this.group.add(this.halos);
  }

  /** Place the sphere about the eye, turn it by the system frame, and set the limit for this frame's sky. */
  update(f: StarSkyFrame): void {
    this.group.position.copy(f.eye); this.group.quaternion.copy(f.frame); this.group.scale.setScalar(f.distance);
    const b = skyBrightness(f.daylight, f.air, f.floor);
    this.limit = limitingMagnitude(b);
    const sunSys = f.sunDir.clone().applyQuaternion(f.frame.clone().invert());
    const glare = f.sunRadius * (f.air > 0 ? 2.5 + 10 * f.air * f.daylight : 2.2);
    const haze = Math.min(0.85, f.daylight * f.air * 0.6);
    const upSys = f.up ? f.up.clone().applyQuaternion(f.frame.clone().invert()) : null;   // (74.10 step 2b: the up in the system frame, as the sun is)
    for (const m of [this.mat, this.haloMat]) {
      m.uniforms.uLimit.value = this.limit; m.uniforms.uScale.value = f.pixelRatio;
      m.uniforms.uSun.value.copy(sunSys); m.uniforms.uGlare.value = glare; m.uniforms.uSky.value.copy(f.skyColor); m.uniforms.uHaze.value = haze;
      if (upSys) m.uniforms.uUp.value.copy(upSys);
      m.uniforms.uDensity.value = upSys && f.density ? f.density : 0;
    }
    let n = 0;
    for (let i = 0; i < this.mags.length; i++) if (this.mags[i] <= this.limit + 0.5 - 1.5 * 0.8) n++;
    this.shown = n;
  }

  /** The draws and points this frame. */
  stats(): { draws: number; points: number; limit: number; shown: number } { return { draws: 2, points: this.mags.length + this.halos.geometry.attributes.position.count, limit: this.limit, shown: this.shown }; }

  dispose(): void { this.points.geometry.dispose(); this.mat.dispose(); this.halos.geometry.dispose(); this.haloMat.dispose(); }
}

/** For a sheet: the limit by time of day under both floors (the table the sheet prints). */
export function limitTable(air: number): Array<{ daylight: number; today: number; proposed: number }> {
  return [0, 0.02, 0.1, 0.3, 1].map(daylight => ({ daylight, today: limitingMagnitude(skyBrightness(daylight, air, NIGHT_FLOOR.today)), proposed: limitingMagnitude(skyBrightness(daylight, air, NIGHT_FLOOR.proposed)) }));
}
export const STAR_COUNT = STAR_FIELD.count + STAR_FIELD.dust;
